import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MarketplaceApp, MarketplaceAppService, VendorService } from '@galaxy/marketplace-apps/v1';
import { ChangeSpendRequestStatus, OrderFulfillmentService } from '@vendasta/order-fulfillment';
import { BehaviorSubject, Observable, Subscription, combineLatest, interval, of } from 'rxjs';
import { catchError, filter, map, shareReplay, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';
import { PARTNER_ID } from '../../tokens';

const VENDOR_APP_LIST_EXPIRATION = 60000; // millisecond

@Injectable({
  providedIn: 'root',
})
export class SpendChangeHasPendingService implements OnDestroy {
  private readonly hasPendingChangeRequests$: Observable<boolean>;
  private vendorAppListNeedsRefresh$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private subscriptions = new Subscription();
  private refresh$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    @Inject(PARTNER_ID) private partnerId$: Observable<string>,
    private router: Router,
    private auth: AuthService,
    private vendorService: VendorService,
    private orderFulfillmentService: OrderFulfillmentService,
    private marketplaceAppService: MarketplaceAppService,
  ) {
    const ticker$ = interval(VENDOR_APP_LIST_EXPIRATION);
    this.subscriptions.add(
      ticker$.subscribe(() => {
        this.vendorAppListNeedsRefresh$$.next(true);
      }),
    );

    const userNavigates$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => true),
      startWith(true),
    );

    const vendorApps$ = combineLatest([this.partnerId$, userNavigates$]).pipe(
      withLatestFrom(this.vendorAppListNeedsRefresh$$),
      filter(([partnerIdAndUserNavigates, vendorAppListNeedsRefresh]) => {
        return partnerIdAndUserNavigates && partnerIdAndUserNavigates[0] && vendorAppListNeedsRefresh;
      }),
      switchMap(([partnerIdAndUserNavigates]) => {
        return this.vendorService.getOwnedAppIdsByPartnerId(partnerIdAndUserNavigates[0]);
      }),
      tap(() => {
        this.vendorAppListNeedsRefresh$$.next(false);
      }),
    );

    this.hasPendingChangeRequests$ = combineLatest([vendorApps$, userNavigates$, this.refresh$$]).pipe(
      switchMap(([appIds]) => {
        return this.marketplaceAppService.getMultiApps(appIds);
      }),
      map((apps: MarketplaceApp[] | null) => {
        return apps?.reduce((acc, app) => {
          if (app.usesCustomPricing) {
            acc = acc.concat(app.appId);
          }
          return acc;
        }, []);
      }),
      switchMap((appIds) => {
        // should return here because querying `listChangeSpendRequests` without appId filter will return any pending change requests.
        if (!appIds || !appIds.length) {
          return of(null);
        }

        return this.orderFulfillmentService.listChangeSpendRequests(
          1,
          '',
          appIds,
          [],
          [ChangeSpendRequestStatus.CHANGE_SPEND_REQUEST_STATUS_CREATED],
        );
      }),
      catchError(() => {
        return of(null);
      }),
      map((resp) => {
        return !!resp?.changeSpendRequests?.length;
      }),
      shareReplay(1),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public get HasPendingChangeRequests(): Observable<boolean> {
    return this.hasPendingChangeRequests$;
  }

  public Refresh(): void {
    this.refresh$$.next(true);
  }
}
